import { useTranslation } from 'react-i18next'
import { AmChartTypes } from '@ublique/components/Charts'
import useColorTheme from './useColorTheme'
import {
  allSeries,
  centralStackedDetailSeries,
  centralStackedSeries
} from '../utils/constants'
import {
  trasformDataForCentralPieChart,
  trasformDataForCentralStackedChart
} from '../utils/dataTransformer'
import { keys } from 'lodash'
import { useMemo } from 'react'

const DOMAIN_NAME = 'DashboardOperativa'

export const useStripeChartsConfig = (ipDataList, filters) => {
  const { t, i18n } = useTranslation('OutputView')
  const { uomFilter, isStacked } = filters
  const { AMCHART_HORIZONTAL_STACKED, AMCHART_PIE } = AmChartTypes
  const colorTheme = useColorTheme()

  const legendLabels = allSeries.reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: t(`${DOMAIN_NAME}.${curr.toUpperCase()}`)
    }),
    {}
  )

  const configPie = () => ({
    xKey: 'category',
    series: ['value'],
    removeLegend: true,
    legendLabel: legendLabels
  })

  const chartXTitle = useMemo(() => {
    if (uomFilter === 'val') return t(`${DOMAIN_NAME}.UOM_VALUE`)
    else if (uomFilter === 'qty') return t(`${DOMAIN_NAME}.UOM_QTY`)
    else if (uomFilter === 'opt') return t(`${DOMAIN_NAME}.UOM_OPT`)
    else return ''
  }, [t, uomFilter])

  const configStacked = (isDetail) => {
    return {
      yKey: 'category',
      customTooltip: true,
      scrollableLegend: true,
      series: isDetail ? centralStackedDetailSeries : centralStackedSeries,
      title: chartXTitle
    }
  }

  const onChartReady = (chart) => {
    if (isStacked) {
      const yAxis = chart.yAxes.getIndex(0)
      yAxis.tooltip.dx = 100

      yAxis.adapter.add('tooltipText', (_, target) => {
        const data = target.tooltipDataItem.dataContext
        let body = ''
        keys(data)
          .filter((v) => v !== 'category')
          .forEach((key) => {
            body += `[${colorTheme[key]}]■[/] ${
              legendLabels[key]
            }: ${formatChartNumber(data, key)}\n`
          })
        return body
      })
    }
  }

  const formatChartNumber = (data, key) => {
    if (!data || !key) return ''
    const value = data[key] ?? 0

    return Math.round(value * 100) / 100
  }

  const getCustomConfig = (ipData, isDetail) => {
    const chartType = isStacked ? AMCHART_HORIZONTAL_STACKED : AMCHART_PIE

    const getConfigFunc = isStacked ? configStacked : configPie
    const chartConfig = getConfigFunc(isDetail)

    const getTransformDataFunct = isStacked
      ? trasformDataForCentralStackedChart
      : trasformDataForCentralPieChart
    const chartData = getTransformDataFunct(
      ipData,
      t,
      DOMAIN_NAME,
      isDetail,
      colorTheme
    )

    return {
      config: chartConfig,
      type: chartType,
      data: chartData,
      legendLabel: legendLabels,
      legendLabels: legendLabels
    }
  }

  return ipDataList
    ?.map((ipData) => {
      return [
        {
          config: {
            sm: 6,
            md: 6,
            lg: 6
          },
          chartProps: {
            ...getCustomConfig(ipData, false),
            chartTitle: t(`${DOMAIN_NAME}.CHART_TITLE`, {
              hyp: ipData?.hyp,
              week: ipData?.week
            }),
            noDataLabel: t('NO_DATA'),
            size: 'md',
            language: i18n,
            chartBackground: false,
            color: colorTheme,
            onChartReady
          }
        },
        {
          config: {
            sm: 6,
            md: 6,
            lg: 6
          },
          chartProps: {
            ...getCustomConfig(ipData, true),
            hideChartTitle: true,
            chartTitle: t(`${DOMAIN_NAME}.CHART_TITLE`, {
              hyp: ipData?.hyp,
              week: ipData?.week
            }),
            noDataLabel: t('NO_DATA'),
            size: 'md',
            language: i18n,
            chartBackground: false,
            color: colorTheme,
            onChartReady
          }
        }
      ]
    })
    .flat()
}
